import React, { useState, useEffect, useRef } from "react";
import { logService } from "../../../services/Logservice";
import "./logs.css";

import {
  Search, Filter, RefreshCw, ChevronLeft, ChevronRight,
  Eye, CheckCircle2, XCircle, Calendar, Layers, Mail, Terminal, X, AlertTriangle
} from "lucide-react";

export default function LogsDashboard() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchInput, setSearchInput] = useState(""); // raw text box value
  const [search, setSearch] = useState("");            // debounced, actually queried value
  const [statusFilter, setStatusFilter] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeModalBody, setActiveModalBody] = useState(null);

  const abortRef = useRef(null);
  const requestIdRef = useRef(0);

  // Debounce the search box -> `search` (this is the only thing that should trigger a refetch)
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Single source of truth for fetching: any relevant state change refetches.
  useEffect(() => {
    const thisRequestId = ++requestIdRef.current;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    logService
      .getAuditLogs({ page, search, status: statusFilter, limit }, { signal: controller.signal })
      .then((data) => {
        // Ignore results from a request that's no longer the latest one.
        if (thisRequestId !== requestIdRef.current) return;
        setLogs(data.results || []);
        setTotalCount(data.count || 0);
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        if (thisRequestId !== requestIdRef.current) return;
        console.error("Dashboard engine failure:", err.message);
        setError("Couldn't load audit logs. Try syncing again.");
        setLogs([]);
        setTotalCount(0);
      })
      .finally(() => {
        if (thisRequestId === requestIdRef.current) setLoading(false);
      });

    return () => controller.abort();
  }, [page, search, statusFilter, limit]);

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      // skip the debounce wait, apply immediately
      setSearch(searchInput.trim());
      setPage(1);
    }
  };

  const handleManualSync = () => {
    // force a refresh of the current page without changing any filter state
    requestIdRef.current += 1; // invalidate in-flight, but keep same deps so effect won't refire on its own
    setLoading(true);
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const thisRequestId = requestIdRef.current;

    logService
      .getAuditLogs({ page, search, status: statusFilter, limit }, { signal: controller.signal })
      .then((data) => {
        if (thisRequestId !== requestIdRef.current) return;
        setLogs(data.results || []);
        setTotalCount(data.count || 0);
        setError(null);
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        setError("Couldn't load audit logs. Try syncing again.");
      })
      .finally(() => setLoading(false));
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  // Clamp page if filters shrink the result set below the current page.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages]); // eslint-disable-line react-hooks/exhaustive-deps

  const getPageNumbers = () => {
    const pages = [];
    const delta = 1;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= page - delta && i <= page + delta)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== "...") {
        pages.push("...");
      }
    }
    return pages;
  };

  return (
    <div className="dashboard-container">

      <div className="header-section">
        <div>
          <h1 className="title-text">System Audit Logs</h1>
          <p className="subtitle-text">Real-time transmission verification matrix for unified notification gateways.</p>
        </div>
        <button className="refresh-btn" onClick={handleManualSync} disabled={loading}>
          <RefreshCw size={15} className={loading ? "spin-animation" : ""} />
          <span>Sync View</span>
        </button>
      </div>

      <div className="filter-bar">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search matching targets... (Press Enter)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          {searchInput && (
            <button
              className="search-clear-btn"
              onClick={() => { setSearchInput(""); setSearch(""); setPage(1); }}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
          <button className="search-submit-btn" onClick={() => { setSearch(searchInput.trim()); setPage(1); }}>
            <Search size={16} />
          </button>
        </div>

        <div className="select-wrapper">
          <Filter size={14} className="filter-icon" />
          <select
            className="dropdown-select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Statuses</option>
            <option value="SENT">Sent</option>
            <option value="FAILED">Failed</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <AlertTriangle size={15} />
          <span>{error}</span>
        </div>
      )}

      <div className="table-wrapper">
        <table className="main-table">
          <thead>
            <tr>
              <th className="th-element">Recipient Tracking Target</th>
              <th className="th-element">Project Scope</th>
              <th className="th-element">Channel Core</th>
              <th className="th-element">Status Flag</th>
              <th className="th-element">Error Message</th>
              <th className="th-element">Dispatched At</th>
              {/* <th className="th-element" style={{ textAlign: "center" }}>Actions</th> */}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: Math.min(limit, 6) }).map((_, i) => (
                <tr key={`skeleton-${i}`} className="tr-row-element">
                  <td className="td-element" colSpan="7">
                    <div className="skeleton-row" />
                  </td>
                </tr>
              ))
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan="7" className="table-message-cell">No network logs recorded matching current parameters.</td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id} className="tr-row-element">
                  <td className="td-element">
                    <div className="primary-cell-text">
                      <Mail size={14} style={{ marginRight: 8, color: "#6366f1" }} />
                      {log.recipient}
                    </div>
                  </td>
                  <td className="td-element">
                    <div className="badge-wrapper">
                      <Layers size={12} style={{ color: "#6366f1", marginRight: 2 }} />
                      <span>{log.project_name || "System Base"}</span>
                    </div>
                  </td>
                  <td className="td-element">
                    <span className="channel-text">{log.channel_type || "EMAIL"}</span>
                  </td>
                  <td className="td-element">
                    <div className={log.status === "SENT" ? "status-sent" : "status-failed"}>
                      {log.status === "SENT" ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                      <span>{log.status}</span>
                    </div>
                  </td>
<td className="td-element">
  {log.error_message ? (
    // 🚀 Only apply the error layout box if an actual error exists
    <div className="error-message">
      {log.error_message}
    </div>
  ) : (
    // # 💡 Clean, professional neutral fallback when status is "SENT"
    <span style={{ color: "#9ca3af", paddingLeft: "4px" }}>—</span>
  )}
</td>
                  <td className="td-element">
                    <div className="date-cell">
                      <Calendar size={13} />
                      {new Date(log.created_at).toLocaleString()}
                    </div>
                  </td>

                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-panel">
        <div className="pagination-left-block">
          <span className="pagination-metrics">
            Showing <b>{logs.length > 0 ? (page - 1) * limit + 1 : 0}</b> to <b>{Math.min(page * limit, totalCount)}</b> of <b>{totalCount}</b> audit logs
          </span>

          <div className="size-select-wrapper">
            <span>Show:</span>
            <select
              className="size-dropdown"
              value={limit}
              onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            >
              <option value={10}>10 records</option>
              <option value={20}>20 records</option>
              <option value={50}>50 records</option>
            </select>
          </div>
        </div>

        <div className="btn-pagination-group">
          <button
            className="pag-nav-btn"
            disabled={page === 1 || loading}
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
          >
            <ChevronLeft size={16} />
          </button>

          {getPageNumbers().map((item, idx) =>
            item === "..." ? (
              <span key={`elipse-${idx}`} className="pag-ellipsis">&hellip;</span>
            ) : (
              <button
                key={`page-${item}`}
                className={`pag-number-btn ${page === item ? "active" : ""}`}
                disabled={loading}
                onClick={() => setPage(item)}
              >
                {item}
              </button>
            )
          )}

          <button
            className="pag-nav-btn"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {activeModalBody && (
        <div className="modal-backdrop" onClick={() => setActiveModalBody(null)}>
          <div className="modal-content-frame" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Terminal size={18} style={{ color: "#6366f1" }} />
                <h3 className="modal-title">Payload Core Sandbox Blueprint</h3>
              </div>
              <button className="close-modal-btn" onClick={() => setActiveModalBody(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <iframe title="Payload Preview Frame" className="sandbox-iframe" srcDoc={activeModalBody} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}